import React, { useCallback, useRef } from 'react';
import { Button, notification } from 'antd';
import { CheckSquareOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../contexts/PermissionsContext';
import { useLiveNotificationFeed } from '../../hooks/useLiveNotificationFeed';
import { workV2Api } from '../../utils/workV2Api';

/** فقط وقتی سوکت وصل نیست: polling پشتیبان روی API */
const POLL_INTERVAL_MS = 45000;
/** هم‌اندازه با فید karmadws */
const LIST_LIMIT = 20;
const SHOWN_KEY = 'work_notification_shown_ids';

const EVENT_TITLES = {
  task_review_requested: 'درخواست بررسی وظیفه',
  task_approved: 'وظیفه شما تأیید شد',
  task_reopened: 'وظیفه شما بازگشایی شد',
  task_assigned: 'وظیفه جدید به شما سپرده شد',
  automation: 'اعلان خودکار وظیفه',
};

const readShown = () => {
  try {
    return new Set(JSON.parse(sessionStorage.getItem(SHOWN_KEY) || '[]'));
  } catch {
    return new Set();
  }
};

const writeShown = (set) => {
  try {
    sessionStorage.setItem(SHOWN_KEY, JSON.stringify([...set].slice(-200)));
  } catch {
    // ذخیره اختیاری است
  }
};

const fetchWorkNotifications = () => workV2Api.notifications.list({ limit: LIST_LIMIT });

/** پاسخ API: { data: [...] } — پاسخ سوکت: { notifications: [...] } */
const normalizeWorkList = (res) => {
  if (Array.isArray(res?.notifications)) return res.notifications;
  if (Array.isArray(res?.data)) return res.data;
  return [];
};

/**
 * اعلان‌های گردش کار (درخواست بررسی، تأیید، بازگشایی، واگذاری) — نمایش به‌صورت toast با لینک به وظیفه.
 * لیست اعلان‌های خوانده‌نشده به صورت زنده از karmadws می‌آید (subscribe-work-notifications /
 * رویداد work_notifications). فقط وقتی سوکت در دسترس نیست، API هر ۴۵ ثانیه poll می‌شود.
 */
const WorkNotificationPoller = () => {
  const { isAuthenticated, token, user } = useAuth();
  const { isModuleActive } = usePermissions();
  const navigate = useNavigate();
  const shownRef = useRef(readShown());

  /** اعلان‌های جدید (نمایش‌داده‌نشده در این تب) را به ترتیب قدیمی به جدید toast می‌کند */
  const showNew = useCallback(
    (items) => {
      items
        .filter((item) => !shownRef.current.has(item.id))
        .reverse()
        .forEach((item) => {
          shownRef.current.add(item.id);
          const key = `work-${item.id}`;
          notification.open({
            key,
            placement: 'bottomLeft',
            duration: 10,
            icon: <CheckSquareOutlined className="text-indigo-500" />,
            message: <span style={{ fontFamily: 'CustomFont' }}>{EVENT_TITLES[item.event] || 'اعلان وظیفه'}</span>,
            description: (
              <div style={{ fontFamily: 'CustomFont' }}>
                <div className="font-medium">{item.heading}</div>
                {item.message ? <div className="text-xs text-gray-500 mt-1">{item.message}</div> : null}
              </div>
            ),
            btn: item.task_id ? (
              <Button
                type="primary"
                size="small"
                onClick={() => {
                  notification.destroy(key);
                  workV2Api.notifications.markRead(item.id).catch(() => {});
                  navigate(`/work/tasks/${item.task_id}`);
                }}
              >
                مشاهده وظیفه
              </Button>
            ) : null,
            onClose: () => workV2Api.notifications.markRead(item.id).catch(() => {}),
          });
        });

      writeShown(shownRef.current);
    },
    [navigate],
  );

  // API بدون ماژول وظایف 403 می‌دهد؛ برای همان کاربران اشتراک هم برقرار نمی‌شود
  useLiveNotificationFeed({
    subscribeEvent: 'subscribe-work-notifications',
    event: 'work_notifications',
    fetchList: fetchWorkNotifications,
    onResponse: (res) => showNew(normalizeWorkList(res)),
    pollIntervalMs: POLL_INTERVAL_MS,
    enabled: Boolean(isAuthenticated && token && !user?.is_superadmin && isModuleActive('tasks')),
    label: 'Work notifications',
  });

  return null;
};

export default WorkNotificationPoller;
